import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';
import { useWhatsAppSession } from '@/hooks/useWhatsAppSession';
import { validateFileSize } from '@/utils/fileValidation';
import { toast } from 'sonner';

// ─── Types ──────────────────────────────────────────────────────────

export interface Message {
  id: string;
  contact_id: string | null;
  lead_id: string | null;
  body: string | null;
  direction: 'inbound' | 'outbound';
  status: string | null;
  error_code: string | null;
  channel: string | null;
  source: string | null;
  sent_at: string | null;
  received_at: string | null;
  created_at: string;
  read_at: string | null;
  sent_by_app_user_id: string | null;
  twilio_message_sid: string | null;
  num_media: number | null;
  media_type: string | null;
  media_storage_path: string | null;
  media_filename: string | null;
  media_mimetype: string | null;
  media_size_bytes: number | null;
  from_e164: string | null;
  to_e164: string | null;
  message_attachments?: MessageAttachment[];
  sent_by?: { display_name: string | null; email: string | null } | null;
  // Metadata
  wasLead?: boolean;
}

export interface MessageAttachment {
  id: string;
  file_name: string;
  content_type: string;
  storage_path: string;
  file_size: number;
  needs_reprocess?: boolean;
  created_at?: string;
  external_url?: string;
}

export interface ContactInfo {
  id: string;
  name: string;
  phone: string;
  avatarUrl?: string;
}

export type ConversationChannel = 'twilio' | 'atendimento';

export interface UseConversationParams {
  contactId: string;
  leadId?: string;
  contactPhone?: string;
  leadPhone?: string;
  initialChannel?: ConversationChannel;
}

export interface UseConversationReturn {
  // State
  messages: Message[];
  filteredMessages: Message[];
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
  isConnected: boolean;

  // Channel
  selectedChannel: ConversationChannel;
  setSelectedChannel: (ch: ConversationChannel) => void;

  // Actions
  sendMessage: (text: string) => Promise<void>;
  sendAudio: (blob: Blob) => Promise<void>;
  sendFile: (file: File) => Promise<void>;
  sendInternalNote: (text: string) => Promise<void>;
  markAsRead: () => Promise<void>;
  reloadMessages: () => Promise<void>;

  // File upload state
  selectedFile: File | null;
  uploadedFileUrl: string | null;
  isUploading: boolean;
  clearSelectedFile: () => void;
  handleFileSelect: (file: File) => Promise<void>;

  // Scroll
  messagesEndRef: React.RefObject<HTMLDivElement>;
  scrollToBottom: () => void;

  // Derived
  phoneNumber: string | undefined;
}

// ─── Helpers ────────────────────────────────────────────────────────

function getTimestamp(msg: Message): number {
  return new Date(msg.sent_at || msg.received_at || msg.created_at).getTime();
}

function sortMessages(msgs: Message[]): Message[] {
  return [...msgs].sort((a, b) => getTimestamp(a) - getTimestamp(b));
}

function isSourceTwilio(source?: string | null, sid?: string | null): boolean {
  const s = source?.toLowerCase();
  return s === 'twilio_divus' || s === 'live' || (!s && !!sid);
}

function isSourceAtendimento(source?: string | null): boolean {
  const s = source?.toLowerCase();
  return s === 'wppconnect_shared' || s === 'whatsapp_import' || s === 'whatsapp_app' || s === 'wppconnect_live';
}

// ─── Hook ───────────────────────────────────────────────────────────

export function useConversation({
  contactId,
  leadId,
  contactPhone,
  leadPhone,
  initialChannel = 'twilio',
}: UseConversationParams): UseConversationReturn {
  const { data: currentAppUser } = useCurrentAppUser();
  const { session: personalSession } = useWhatsAppSession();

  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<ConversationChannel>(initialChannel);

  // File upload
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
  const [uploadedFilePath, setUploadedFilePath] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageCountRef = useRef(0);

  const phoneNumber = contactPhone || leadPhone;

  // ─── Filter messages by channel ───────────────────────────────
  const filteredMessages = useMemo(() => {
    return messages.filter(msg => {
      // Internal notes always show regardless of channel
      if (msg.source === 'internal_note') return true;
      if (selectedChannel === 'twilio') {
        return isSourceTwilio(msg.source, msg.twilio_message_sid);
      }
      if (selectedChannel === 'atendimento') {
        return isSourceAtendimento(msg.source);
      }
      return true;
    });
  }, [messages, selectedChannel]);

  // ─── Scroll ───────────────────────────────────────────────────
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const isNearBottom = useCallback(() => {
    if (!messagesEndRef.current) return true;
    const container = messagesEndRef.current.parentElement;
    if (!container) return true;
    return container.scrollHeight - container.scrollTop - container.clientHeight < 150;
  }, []);

  // ─── Mark as read ─────────────────────────────────────────────
  const markAsRead = useCallback(async () => {
    try {
      let query = supabase
        .from('messages')
        .update({ read_at: new Date().toISOString() })
        .eq('direction', 'inbound')
        .is('read_at', null);

      if (leadId && contactId) {
        query = query.or(`contact_id.eq.${contactId},lead_id.eq.${leadId}`);
      } else if (contactId) {
        query = query.eq('contact_id', contactId);
      } else if (leadId) {
        query = query.eq('lead_id', leadId);
      }

      await query;
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  }, [contactId, leadId]);

  // ─── Load messages ────────────────────────────────────────────
  const loadMessages = useCallback(async (showLoader = true) => {
    if (showLoader) setIsLoading(true);
    setError(null);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('twilio-whatsapp', {
        body: {
          action: 'getMessages',
          contactId: contactId || null,
          leadId: leadId || null,
        },
      });

      if (invokeError) throw invokeError;

      if (data?.success) {
        const sorted = sortMessages(data.messages || []);

        if (sorted.length > lastMessageCountRef.current && isNearBottom()) {
          setTimeout(scrollToBottom, 100);
        }

        lastMessageCountRef.current = sorted.length;
        setMessages(sorted);
        setIsConnected(true);
        markAsRead();

        if (showLoader) {
          setTimeout(scrollToBottom, 100);
        }
      } else {
        setError(data?.error || 'Failed to load messages');
        setIsConnected(false);
      }
    } catch (err: any) {
      console.error('Error loading messages:', err);
      setError(err.message || 'Failed to load messages');
      setIsConnected(false);
    } finally {
      if (showLoader) setIsLoading(false);
    }
  }, [contactId, leadId, scrollToBottom, isNearBottom, markAsRead]);

  // Initial load
  useEffect(() => {
    if (contactId || leadId) {
      loadMessages(true);
    }
  }, [contactId, leadId, loadMessages]);

  // ─── Realtime ─────────────────────────────────────────────────
  useEffect(() => {
    if (!contactId && !leadId) return;

    const filter = contactId
      ? `contact_id=eq.${contactId}`
      : `lead_id=eq.${leadId}`;

    const channel = supabase
      .channel(`chat-messages-${contactId || leadId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter }, (payload) => {
        setMessages(prev => {
          if (prev.some(m => m.id === payload.new.id)) return prev;
          const updated = sortMessages([...prev, payload.new as Message]);
          lastMessageCountRef.current = updated.length;
          return updated;
        });
        if (isNearBottom()) setTimeout(scrollToBottom, 100);
        markAsRead();
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'messages', filter }, (payload) => {
        setMessages(prev =>
          prev.map(msg => (msg.id === payload.new.id ? { ...msg, ...payload.new } : msg))
        );
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [contactId, leadId, scrollToBottom, isNearBottom, markAsRead]);

  // ─── Provider routing ─────────────────────────────────────────
  const getProvider = useCallback(() => {
    if (selectedChannel === 'twilio') return 'twilio' as const;
    return 'wppconnect' as const;
  }, [selectedChannel]);

  const resolveSharedFunction = async (): Promise<string> => {
    const { data: sharedSession } = await supabase
      .from('shared_whatsapp_session')
      .select('provider')
      .eq('is_active', true)
      .maybeSingle();
    return sharedSession?.provider === 'evolution' ? 'evolution-whatsapp' : 'wppconnect-whatsapp';
  };

  const ensureSupportSession = async (): Promise<string | null> => {
    if (selectedChannel !== 'atendimento' || !contactId) return null;

    const { data: active } = await supabase
      .from('support_sessions')
      .select('id')
      .eq('contact_id', contactId)
      .neq('status', 'resolved')
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (active) return active.id;

    const { data: newId } = await supabase.rpc('create_sibling_session', {
      p_contact_id: contactId,
      p_demand_type: 'status',
      p_assigned_to: currentAppUser?.id || null,
    });
    return newId;
  };

  // ─── File upload ──────────────────────────────────────────────
  const handleFileSelect = useCallback(async (file: File) => {
    const validation = validateFileSize(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    try {
      setIsUploading(true);
      setSelectedFile(file);

      const fileName = `${crypto.randomUUID()}_${file.name}`;
      const { data, error } = await supabase.storage
        .from('whatsapp-media')
        .upload(`outbound/${fileName}`, file, { cacheControl: '3600', upsert: false });

      if (error) throw error;

      const { data: signedUrlData } = await supabase.storage
        .from('whatsapp-media')
        .createSignedUrl(data.path, 3600);

      setUploadedFileUrl(signedUrlData!.signedUrl);
      setUploadedFilePath(data.path);
    } catch (err: any) {
      console.error('Error uploading file:', err);
      toast.error('Erro ao fazer upload: ' + err.message);
      setSelectedFile(null);
    } finally {
      setIsUploading(false);
    }
  }, []);

  const clearSelectedFile = useCallback(() => {
    setSelectedFile(null);
    setUploadedFileUrl(null);
    setUploadedFilePath(null);
  }, []);

  // ─── Save attachment metadata ─────────────────────────────────
  const saveAttachmentMetadata = async (messageId: string, file: File, storagePath: string) => {
    try {
      await supabase.from('message_attachments').insert({
        message_id: messageId,
        file_name: file.name,
        content_type: file.type,
        file_size: file.size,
        storage_path: storagePath,
      });
    } catch (err) {
      console.error('Error saving attachment metadata:', err);
    }
  };

  // ─── Send via provider ────────────────────────────────────────
  interface SendMediaMeta {
    mediaUrl?: string | null;
    storagePath?: string | null;
    filename?: string | null;
    mimetype?: string | null;
    fileSize?: number | null;
  }

  const invokeSend = async (body: string, media?: SendMediaMeta | string | null) => {
    // Backward-compat: allow string mediaUrl
    const meta: SendMediaMeta = typeof media === 'string' || media == null
      ? { mediaUrl: (media as string) || null }
      : media;

    const provider = getProvider();
    let data: any, invokeError: any;

    if (provider === 'wppconnect') {
      const functionName = await resolveSharedFunction();
      const sessionId = await ensureSupportSession();

      const response = await supabase.functions.invoke(functionName, {
        body: {
          action: 'send',
          to: phoneNumber,
          body,
          contactId: contactId || null,
          leadId: leadId || null,
          mediaUrl: meta.mediaUrl || null,
          filename: meta.filename || null,
          mimetype: meta.mimetype || null,
          storagePath: meta.storagePath || null,
          fileSize: meta.fileSize ?? null,
          useShared: true,
          sessionId,
        },
      });
      data = response.data;
      invokeError = response.error;
    } else {
      const response = await supabase.functions.invoke('twilio-whatsapp', {
        body: {
          action: 'sendMessage',
          to: phoneNumber,
          body,
          contactId: contactId || null,
          leadId: leadId || null,
          mediaUrl: meta.mediaUrl || null,
          filename: meta.filename || null,
          mimetype: meta.mimetype || null,
          storagePath: meta.storagePath || null,
          fileSize: meta.fileSize ?? null,
        },
      });
      data = response.data;
      invokeError = response.error;
    }

    if (invokeError) throw invokeError;
    return data;
  };

  // ─── Send message ─────────────────────────────────────────────
  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() && !selectedFile) return;
    if (!phoneNumber) return;

    setIsSending(true);
    setError(null);

    try {
      const body = text.trim() || (selectedFile ? `📎 ${selectedFile.name}` : '📎 Arquivo anexado');
      const data = await invokeSend(body, {
        mediaUrl: uploadedFileUrl,
        storagePath: uploadedFilePath,
        filename: selectedFile?.name || null,
        mimetype: selectedFile?.type || null,
        fileSize: selectedFile?.size ?? null,
      });

      if (data?.success) {
        // Save redundant attachment metadata for both providers (twilio returns
        // message: { id }, wppconnect returns messageId).
        const insertedId = data.message?.id || data.messageId || null;
        if (selectedFile && uploadedFilePath && insertedId) {
          await saveAttachmentMetadata(insertedId, selectedFile, uploadedFilePath);
        }
        clearSelectedFile();
        scrollToBottom();
      } else {
        const isDisconnected = data?.code === 'WHATSAPP_CENTRAL_DISCONNECTED'
          || /connection closed|whatsapp central desconectado/i.test(String(data?.error || ''));
        const errorMsg = isDisconnected
          ? 'WhatsApp Central desconectado. Reconecte a sessão em Configurações → WhatsApp.'
          : (data?.error || 'Falha ao enviar mensagem');
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err: any) {
      console.error('Error sending message:', err);
      const rawMsg = err?.message || err?.error || 'Falha ao enviar mensagem';
      const isDisconnected = err?.code === 'WHATSAPP_CENTRAL_DISCONNECTED'
        || /connection closed|whatsapp central desconectado|non-2xx|409/i.test(String(rawMsg));
      const errorMsg = isDisconnected
        ? 'WhatsApp Central desconectado. Reconecte a sessão em Configurações → WhatsApp.'
        : rawMsg;
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsSending(false);
    }
  }, [phoneNumber, selectedFile, uploadedFileUrl, uploadedFilePath, clearSelectedFile, scrollToBottom]);

  // ─── Send audio ───────────────────────────────────────────────
  const sendAudio = useCallback(async (audioBlob: Blob) => {
    if (!audioBlob || !phoneNumber) return;

    try {
      const fileName = `audio-${Date.now()}.mp3`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('whatsapp-media')
        .upload(`outbound/${fileName}`, audioBlob, {
          cacheControl: '3600',
          upsert: false,
          contentType: 'audio/mpeg',
        });

      if (uploadError) throw uploadError;

      const { data: publicUrlData } = supabase.storage
        .from('whatsapp-media')
        .getPublicUrl(uploadData.path);

      const mediaUrl = publicUrlData?.publicUrl;
      if (!mediaUrl) throw new Error('Falha ao obter URL do áudio');

      const data = await invokeSend('🎤 Mensagem de voz', mediaUrl);

      if (data?.success) {
        if (data.message?.id) {
          await supabase.from('message_attachments').insert({
            message_id: data.message.id,
            file_name: fileName,
            content_type: 'audio/mpeg',
            file_size: audioBlob.size,
            storage_path: uploadData.path,
          });
        }
        scrollToBottom();
        toast.success('Áudio enviado');
      } else {
        throw new Error(data?.error || 'Falha ao enviar áudio');
      }
    } catch (err: any) {
      console.error('Error sending audio:', err);
      toast.error('Erro ao enviar áudio: ' + (err.message || 'Erro desconhecido'));
    }
  }, [phoneNumber, scrollToBottom]);

  // ─── Send file (shortcut) ────────────────────────────────────
  const sendFile = useCallback(async (file: File) => {
    await handleFileSelect(file);
    // File will be sent with next sendMessage call
  }, [handleFileSelect]);

  // ─── Send internal note ───────────────────────────────────────
  const sendInternalNote = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (trimmed.length > 2000) {
      toast.error('Nota muito longa (máx. 2000 caracteres)');
      return;
    }
    if (!currentAppUser?.id) {
      toast.error('Usuário não identificado');
      return;
    }
    if (!contactId && !leadId) {
      toast.error('Conversa sem destinatário');
      return;
    }

    setIsSending(true);
    setError(null);
    try {
      const { error: insertError } = await supabase.from('messages').insert({
        contact_id: contactId || null,
        lead_id: leadId || null,
        channel: 'whatsapp',
        direction: 'outbound',
        source: 'internal_note',
        body: trimmed,
        sent_by_app_user_id: currentAppUser.id,
        sent_at: new Date().toISOString(),
        status: 'sent',
      });

      if (insertError) throw insertError;

      await loadMessages(false);
      scrollToBottom();
    } catch (err: any) {
      console.error('Error sending internal note:', err);
      const msg = err?.message || 'Falha ao salvar nota interna';
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSending(false);
    }
  }, [contactId, leadId, currentAppUser?.id, scrollToBottom]);

  return {
    messages,
    filteredMessages,
    isLoading,
    isSending,
    error,
    isConnected,
    selectedChannel,
    setSelectedChannel,
    sendMessage,
    sendAudio,
    sendFile,
    sendInternalNote,
    markAsRead,
    reloadMessages: () => loadMessages(false),
    selectedFile,
    uploadedFileUrl,
    isUploading,
    clearSelectedFile,
    handleFileSelect,
    messagesEndRef,
    scrollToBottom,
    phoneNumber,
  };
}
